import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';

import classNames from 'classnames';
import { Form, FormControl } from 'components/form';
import { InputGroup, EditorGroup, MediaUploadGroup } from 'components/input';
import { Loader } from 'components/loader';
import { translate } from 'components/translate';
import { ROUTES } from 'configs/routes/constants';
import {
  showSurvey,
  invokeEvent,
  SURVEY_EVENTS,
  SURVEY_ID_LIST
} from 'configs/Survicate';
import { ShellContext } from 'contexts/shellContext';
import Currency from 'helpers/Currency';
import { rawText, rawTextLength } from 'helpers/String';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { DatahubEventsService } from 'modules/app/services';
import { DatahubModuleEditorService } from 'modules/app/services/datahub/moduleEditor';
import { ClickModuleEditorSaveSchema } from 'modules/app/services/datahubEvents/module/clickModuleEditorSaveSchema';
import { ViewModuleEditorSchema } from 'modules/app/services/datahubEvents/module/viewModuleEditorSchema';
import ContentBasePage from 'modules/content/components/ContentBasePage';
import ContentPreventLeave from 'modules/content/components/ContentPreventLeave';
import { MODULE_NAME_MAX_CHARACTERS } from 'modules/content/constants';
import { getModulePageTitle } from 'modules/content/contentPageTitles';
import { FeatureToggles } from 'modules/featureToggle';
import { FileService } from 'modules/file';
import { ENTITY_TYPE } from 'modules/file/constants';
// eslint-disable-next-line no-restricted-imports
import { onlineEventLabelHandler } from 'modules/onlineEvent/helpers';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import ClassesList from './ClassesList';
import { ModuleEditorFree } from './free';
import { ModuleCover } from './moduleCover';
import { ModuleDisplayAlert } from './moduleCover/moduleDisplayAlert';
import ModuleSelect from './select/ModuleSelect';
import { ModuleType } from './select/ModuleTypes';

import ContentService from '../../ContentService';
import ModuleEditorParamsUtils from '../ModuleEditorParamsUtils';
import ModuleEditorService from '../ModuleEditorService';

import { uploadNotifySave } from '@hotmart/club-files-ui';

import './ModuleEditor.scss';

const MAX_HEIGHT_OFFSET = 2000;
const DESCRIPTION_MAX_CHARACTERS = 1000;

@inject((stores) => ({
  MembershipStore: stores.MembershipStore,
  ModuleEditorStore: stores.ModuleEditorStore,
  shortLocale: stores.UserStore.shortLocale,
  isActivationFreeSignupEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.ActivateFreeSignup
  ),
  createModuleConceptEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.CreateModuleConcept
  )
}))
@translate()
@withRouter
@observer
class ModuleEditor extends Component {
  static contextType = ShellContext;

  // #region setup
  static propTypes = {
    MembershipStore: MobxPropTypes.objectOrObservableObject,
    ModuleEditorStore: MobxPropTypes.objectOrObservableObject,
    history: PropTypes.object,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string
      })
    }),
    isActivationFreeSignupEnabled: PropTypes.bool
  };

  state = {
    loaded: true,
    maxHeight: 0,
    firstLoad: false
  };

  constructor(props) {
    super(props);

    this.childWrapper = React.createRef();
    this.formNode = React.createRef();
  }

  componentDidMount() {
    const arrivedFrom = new URLSearchParams(window.location.search).get(
      'arrivedFrom'
    );
    const productId = new URLSearchParams(window.location.search).get(
      'productId'
    );
    const datahubViewSchema = new ViewModuleEditorSchema({
      arrivedFrom,
      productId,
      path: this.props.match.url
    });
    let extra = ModuleEditorParamsUtils.getExtraParam();
    let paidModule = ModuleEditorParamsUtils.getExtraPaidParam();
    const season = ModuleEditorParamsUtils.getSeasonParam();
    let moduleType = extra ? ModuleType.Extra : ModuleType.Standard;

    DatahubEventsService.sendEvent({ schema: datahubViewSchema });

    if (paidModule || window.location.href.includes('extra-paid')) {
      moduleType = ModuleType.Paid;
      extra = true;
      paidModule = true;
    }

    ModuleEditorService.init(
      { extra, paidModule, season, moduleType },
      this.props.history
    );
    this.setInitialStateAsync();
  }

  componentWillUnmount() {
    ModuleEditorService.clear();
  }

  async setInitialStateAsync() {
    const {
      params: { id }
    } = this.props.match;

    if (id) {
      await ModuleEditorService.get(id);
    }

    this.setState({ firstLoad: true });
  }

  getEventSpecificLabels = (label) => {
    const { onlineEvent } = this.props.MembershipStore;

    const EVENT_LABELS_DICTIONARY = {
      'module_editor.title': 'online_event.module_editor.new_title',
      'general.classes': 'online_event.general.access_definitions',
      'general.content': 'online_event.module_editor.new_module_info',
      'general.class_tooltip': 'online_event.general.group_access_info'
    };

    const specificLabel = EVENT_LABELS_DICTIONARY[label];

    return onlineEvent ? specificLabel : label;
  };
  // #endregion

  sendModuleCoverEvent = (id, moduleActionType) => {
    const { coverImageId } = this.props.ModuleEditorStore;

    if (coverImageId) {
      DatahubModuleEditorService.sendModuleCoverEvent({
        category: id,
        url: window.location,
        eventAction: `${moduleActionType}Module`
      });
    }
  };

  // #region page actions
  handleSubmit = async (data) => {
    this.toggleLoader();

    const productId = new URLSearchParams(window.location.search).get(
      'productId'
    );
    const datahubViewSchema = new ClickModuleEditorSaveSchema({
      productId,
      path: this.props.match.url
    });

    DatahubEventsService.sendEvent({ schema: datahubViewSchema });

    const { ModuleEditorStore } = this.props;
    const moduleActionType = this.actionType;
    const response = await ModuleEditorService[this.actionType]({
      ...data,
      classes: ModuleEditorStore.classes,
      coverImageId: ModuleEditorStore.coverImageId
    });

    if (response) {
      const seasonId = ModuleEditorParamsUtils.getSeasonParam();
      const { isMembership } = this.context;
      const { id } = response;

      await ContentService.fetchModulesList(
        seasonId,
        ModuleEditorService.extra
      );

      if (isMembership) {
        showSurvey(SURVEY_ID_LIST.CONTENT_MANAGEMENT);
      }

      this.sendModuleCoverEvent(ModuleEditorStore?.id, moduleActionType);

      if (id) {
        const { extra, paidModule, publicModule } = ModuleEditorStore;

        uploadNotifySave(id);

        DatahubModuleEditorService.sendCreateModule({
          extra,
          paidModule,
          publicModule,
          url: window.location.search
        });
        ContentService.lastCreatedModule = id;

        invokeEvent(SURVEY_EVENTS.OLD_MODULE_CREATE);
      } else {
        ContentService.lastEditedModule = ModuleEditorStore?.id;
      }
    }

    this.toggleLoader();

    if (response !== false) {
      const queryParams = new URLSearchParams(this.props.location.search);

      queryParams.set('extra', ModuleEditorService.extra);
      this.props.history.push({
        pathname: ROUTES.CONTENT.LIST,
        search: queryParams.toString()
      });
    }
  };

  handleBack = () => {
    const { createModuleConceptEnabled, history, match } = this.props;
    const isExtraPaid =
      createModuleConceptEnabled && window.location.href.includes('extra-paid');

    if (isExtraPaid) {
      const redirectUrl = match.params?.id
        ? `${ROUTES.CONTENT.LIST}?extra=true`
        : `${ROUTES.CONTENT.NEW_MODULE}?moduleType=paidModule`;

      history.push(redirectUrl);
      return;
    }

    history.push(ROUTES.CONTENT.LIST);
  };

  handleCancel = () => {
    const { history } = this.props;

    history.push(ROUTES.CONTENT.LIST);
  };
  // #endregion

  // #region getters
  get actionType() {
    return this.props.ModuleEditorStore.id ? 'update' : 'create';
  }

  onError = (message, data) => UiService.setAlertError(message, data);

  maxHeight(opened) {
    if (!opened) {
      return 0;
    }

    const { scrollHeight } = this.childWrapper || {};
    const contentIsBiggerThanMaxHeight = scrollHeight > MAX_HEIGHT_OFFSET;

    return contentIsBiggerThanMaxHeight
      ? // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        scrollHeight * 1.1
      : MAX_HEIGHT_OFFSET;
  }
  // #endregion

  // #region setters
  setMediaUrl = (mediaUrl) => {
    ModuleEditorService.mediaVideoUrl = mediaUrl;
  };

  toggleLoader = () => {
    this.setState((prevState) => ({ loaded: !prevState.loaded }));
  };

  syncFormChanges = (inputs) => {
    Object.assign(this.props.ModuleEditorStore, { ...inputs });
  };
  // #endregion
  getDescription = (remainingCharacters) => {
    const { t } = this.props;
    const classCharacteres =
      remainingCharacters < 0 ? 'char__limit_exceeded' : 'char__limit';

    return (
      <label className="description">
        {t('general.description')}

        <span className={classCharacteres}>
          {`${remainingCharacters} `}
          {t('general.characters')}
        </span>
      </label>
    );
  };

  renderFreeModule = () => {
    const { extra, publicModule } = this.props.ModuleEditorStore;
    const { academy } = this.props.MembershipStore;
    const { isActivationFreeSignupEnabled } = this.props;

    if (academy || extra) {
      return null;
    }

    return (
      <FormControl>
        <ModuleEditorFree
          name="publicModule"
          value={publicModule}
          isActivationFreeSignupEnabled={isActivationFreeSignupEnabled}
        />
      </FormControl>
    );
  };

  renderNotAcademy() {
    const {
      extra,
      paidModule,
      description,
      offerPrice,
      mediaVideoUrl,
      media,
      image
    } = this.props.ModuleEditorStore;
    const { currencyCode, subscription } = this.props.MembershipStore;
    const remainingCaracteres =
      DESCRIPTION_MAX_CHARACTERS - rawTextLength(description);
    const hasShowDescription = (context) =>
      extra && (context.state.inputs.paidModule || paidModule);

    return (
      <Fragment>
        {extra && !subscription && (
          <Fragment>
            <Form.Context>
              {(context) => (
                <div
                  ref={this.childWrapper}
                  style={{
                    maxHeight: this.maxHeight(hasShowDescription(context))
                  }}
                  className={classNames('module__extra', {
                    'module__extra--show': hasShowDescription(context)
                  })}
                >
                  {this.getDescription(remainingCaracteres)}
                  <FormControl>
                    <EditorGroup
                      required
                      name="description"
                      value={description}
                      placeholder="general.editor_placeholder"
                      validate={
                        context.state.inputs.paidModule === true || paidModule
                      }
                      maxLength={DESCRIPTION_MAX_CHARACTERS}
                      getter={rawText}
                    />
                  </FormControl>

                  <FormControl>
                    <InputGroup
                      min={1}
                      required
                      size="sm"
                      name="offerPrice"
                      autoComplete="off"
                      value={offerPrice}
                      labelValue="general.price"
                      mask={Currency.currencyMaskFn}
                      icon={Currency.symbol(currencyCode)}
                      validate={
                        context.state.inputs.paidModule === true || paidModule
                      }
                      placeholder="module_editor.paid_module_price_placeholder"
                    />
                  </FormControl>

                  <FormControl>
                    <MediaUploadGroup
                      name="media"
                      labelValue="general.media"
                      description="general.choose_media"
                      entityType={ENTITY_TYPE.MODULE_PURCHASE_DATA}
                      value={media || image}
                      mediaUrl={mediaVideoUrl}
                      setMediaUrl={this.setMediaUrl}
                      onCancelUpload={FileService.onCancelUpload}
                      setCentralError={this.onError}
                    />
                  </FormControl>
                </div>
              )}
            </Form.Context>
          </Fragment>
        )}
      </Fragment>
    );
  }

  renderModuleTypeOnSinglePage() {
    const { createModuleConceptEnabled, MembershipStore, ModuleEditorStore } =
      this.props;
    const { isMembership } = this.context;

    if (createModuleConceptEnabled && isMembership) {
      return null;
    }

    return (
      <FormControl>
        <ModuleSelect
          name="moduleType"
          actionType={this.actionType}
          value={ModuleEditorStore.moduleType}
          isSubscription={MembershipStore.subscription}
        />
      </FormControl>
    );
  }

  renderModuleCover = () => {
    const { shortLocale } = this.props;
    const { isMembership } = this.context;

    if (isMembership) {
      return <ModuleCover />;
    }

    return <ModuleDisplayAlert isOldClub shortLocale={shortLocale} />;
  };

  render() {
    const { isMembership } = this.context;

    if (!this.state.firstLoad) {
      return <Loader fixed />;
    }

    const {
      createModuleConceptEnabled,
      ModuleEditorStore: { name, hotPassCost, snapshot },
      MembershipStore: { academy, hasSeason, onlineEvent },
      match: { params },
      t
    } = this.props;

    const isExtraPaidModule = window.location.href.includes('extra-paid');
    const formTitle = getModulePageTitle({
      isMembership,
      isExtraPaidModule,
      isEditingItem: params?.id,
      createModuleConceptEnabled
    });

    return (
      <Form
        className="module__editor"
        ref={this.formNode}
        onSubmit={this.handleSubmit}
        onChange={this.syncFormChanges.bind(this)}
      >
        <ContentBasePage
          className="module__editor__container content__grid"
          backHandler={this.handleBack}
          cancelHandler={this.handleCancel}
        >
          <ContentBasePage.Header
            backHandler={this.handleBack}
            title={this.getEventSpecificLabels(formTitle)}
          />
          {onlineEvent && (
            <div className="mb__md">
              {t('online_event.module_editor.new_subtitle')}
            </div>
          )}
          <div>
            <section>
              <div className="_mb-8">
                <label
                  className="hot-form__label _text-2 _font-weight-bold _text-gray-900"
                  htmlFor="name"
                >
                  {t('general.name')}
                </label>
                <FormControl>
                  <InputGroup
                    required
                    ref={this.inputNameRef}
                    name="name"
                    value={name}
                    maxLength={MODULE_NAME_MAX_CHARACTERS}
                    autoComplete="off"
                    placeholder={onlineEventLabelHandler(
                      'module_editor.module_name'
                    )}
                    className=""
                    inputClassName="hot-form__input"
                  />
                </FormControl>
              </div>

              {this.renderModuleTypeOnSinglePage()}

              {this.renderModuleCover()}

              <ClassesList />

              {academy && (
                <FormControl>
                  <InputGroup
                    size="sm"
                    className="mb__0"
                    inputClassName="input"
                    name="hotPassCost"
                    autoComplete="off"
                    value={hotPassCost}
                    labelValue="general.hot_pass"
                    description="module_editor.hot_pass_cost"
                    mask={[
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/
                    ]}
                  />
                </FormControl>
              )}

              {!hasSeason && this.renderNotAcademy()}
            </section>
            {this.renderFreeModule()}
          </div>

          <ContentBasePage.Footer />
        </ContentBasePage>

        {!this.state.loaded && <Loader fixed />}

        <ContentPreventLeave
          snapshot={snapshot}
          current={ModuleEditorService.current}
          onConfirm={this.formNode && this.formNode.submitAndReturn}
        />
      </Form>
    );
  }
}

export default ModuleEditor;
